import { useState, useEffect, createContext, useContext } from 'react';

const PROD_FALLBACK_API_URL = 'https://subsbackend.kito.pet';
const ENV_API_URL = process.env.REACT_APP_API_URL;

// Fail-fast in local so we never silently hit the wrong backend.
if ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && !ENV_API_URL) {
  throw new Error(
    'REACT_APP_API_URL is required on localhost. ' +
    'Set it (e.g. http://localhost:8000) and restart the dev server.'
  );
}

// IMPORTANT: Never default to localhost in production builds.
// If REACT_APP_API_URL is not set on Amplify, a localhost fallback will trigger
// Chrome\'s "Local Network Access" permission prompt on page load.
const API_URL = ENV_API_URL || PROD_FALLBACK_API_URL;

/**
 * Get the domain to identify the company.
 *
 * Production → full hostname (e.g. "abone.kitopet.com")
 * Localhost  → REACT_APP_CUSTOM_DOMAIN env var (simulates a custom domain)
 */
const getCompanyDomain = () => {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_CUSTOM_DOMAIN || null;
  }

  return hostname;
};

const DEFAULT_BRANDING = {
  name: '',
  logoUrl: null,
  companyLogo: null,
  primaryColor: '#222222',
  secondaryColor: '#ffffff',
  brandColor: null,
  tagline: '',
  heroTitle: '',
  heroSubtitle: '',
};

// ── Context ────────────────────────────────────────────────────────────────
const BrandingContext = createContext({
  branding: DEFAULT_BRANDING,
  loading: true,
  domain: null,
  notFound: false,
  error: null,
});

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);
  const domain = getCompanyDomain();

  useEffect(() => {
    // Domain çözümlenemediyse tenant'ı belirleyemiyoruz; varsayılan branding ile
    // ilerlemek yerine "bulunamadı" akışını tetikle ki UI kullanıcıyı bilgilendirsin.
    if (!domain) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchBranding = async () => {
      try {
        const res = await fetch(
          `${API_URL}/public/branding?domain=${encodeURIComponent(domain)}`
        );
        if (res.status === 404) {
          if (!cancelled) setNotFound(true);
          return;
        }
        if (!res.ok) throw new Error(`Branding fetch failed: ${res.status}`);
        const data = await res.json();
        if (!cancelled && data.success) {
          setBranding(data.branding);
        } else if (!cancelled) {
          setNotFound(true);
        }
      } catch (err) {
        console.warn('[Branding] Could not load company branding:', err.message);
        // Network/5xx hatalarını notFound'dan ayır — kullanıcıya doğru mesaj gösterilsin
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchBranding();
    return () => { cancelled = true; };
  }, [domain]);

  return (
    <BrandingContext.Provider value={{ branding, loading, domain, notFound, error }}>
      {children}
    </BrandingContext.Provider>
  );
};

// ── Hook ───────────────────────────────────────────────────────────────────
export const useBranding = () => useContext(BrandingContext);

export default BrandingContext;
